import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, TextField, Autocomplete, Chip, Slider, CircularProgress,
  IconButton, Button, Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import LinkIcon from '@mui/icons-material/Link';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CloseIcon from '@mui/icons-material/Close';
import { db } from '../../../../firebase/config';
import { collection, doc, getDoc, getDocs, orderBy, query } from 'firebase/firestore';
import { useAuth } from '../../../../contexts/AuthContext';
import { APPLAB_ACCENT, APPLAB_ORANGE } from '../../constants';
import { saveDraft, loadDraft, createMockupApp } from '../services/mockupService';
import BrandingSection from '../components/branding/BrandingSection';
import { useSoftwareTags } from '../../../../hooks/useSoftwareTags';

// ── Konstanter ────────────────────────────────────────────────────────────────
const SITUATIONS = [
  { id: 'early',    label: 'Tidig dialog',  icon: '🌱', desc: 'Första mötet, visar möjligheter' },
  { id: 'proposal', label: 'Konkret offert', icon: '📋', desc: 'Lösning kopplad till ett uppdrag' },
  { id: 'active',   label: 'Pågående projekt', icon: '🏗️', desc: 'Statusrapport eller ny funktion' },
];

const PRESETS = [
  { id: 'polished',  label: 'Polerat',    icon: '💼', desc: 'Affärsmässigt, mjuka toner, subtila skuggor' },
  { id: 'technical', label: 'Tekniskt',   icon: '🖥️', desc: 'Dark mode, datafokuserat, monospace' },
  { id: 'minimal',   label: 'Minimalt',   icon: '◻️', desc: 'Luftigt, ren typografi, lite dekoration' },
  { id: 'premium',   label: 'Premium',    icon: '✦', desc: 'Mörkt, guld/silver-accenter, exklusivt' },
];


const BRIEF_EXAMPLES = {
  early:    'Visa hur ett digitalt projektuppföljningssystem kan se ut för deras infrastrukturprojekt. Inkludera milstolpar, resursstatus och dokumentöversikt.',
  proposal: 'En dashboard för att följa byggnationen av deras nya logistikcenter i Göteborg. Visa kostnadskontroll, tidsplan och avvikelserapportering.',
  active:   'Uppdaterad statusrapport för fas 2 av renoverings­projektet. Visa slutförda och pågående arbetsorder, risklista och ekonomisk prognos.',
};

const DEFAULT_DRAFT = {
  context:  { customer_name: '', customer_id: null, uppdrag_id: null, situation: 'early' },
  brief:    { description: '', reference_files: [], reference_links: [] },
  style:    { preset: 'polished', colors: { detected: null, selected: null, mode: 'auto' }, tone: 0 },
  branding: { customer_branding_ref: null, adherence: 'guided', selected_colors: [], selected_fonts: { heading: '', body: '' } },
  systems:  { registered: [], custom: [] },
};

// ── Hjälp-komponenter ─────────────────────────────────────────────────────────
function StepIndicator({ current, total }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Array.from({ length: total }).map((_, i) => (
        <React.Fragment key={i}>
          <Box sx={{
            width: 28, height: 28, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: i < current ? APPLAB_ACCENT : i === current ? 'white' : 'rgba(255,255,255,0.1)',
            color: i < current ? 'white' : i === current ? '#111' : '#555',
            fontSize: '0.75rem', fontWeight: 700,
          }}>
            {i < current ? <CheckIcon sx={{ fontSize: 14 }} /> : i + 1}
          </Box>
          {i < total - 1 && (
            <Box sx={{ flex: 1, height: 2, bgcolor: i < current ? APPLAB_ACCENT : 'rgba(255,255,255,0.08)', borderRadius: 1 }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

// ── Steg 1: Kontext ───────────────────────────────────────────────────────────
function Step1({ draft, setDraft }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    getDocs(query(collection(db, 'crmCompanies'), orderBy('name')))
      .then(snap => setCompanies(snap.docs.map(d => ({ id: d.id, name: d.data().name || '' })).filter(c => c.name)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function set(key, val) { setDraft(d => ({ ...d, context: { ...d.context, [key]: val } })); }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography fontWeight={700} sx={{ color: 'white', mb: 0.5, fontSize: '0.9rem' }}>Kund eller organisation *</Typography>
        <Typography sx={{ color: '#666', fontSize: '0.8rem', mb: 1.5 }}>Vilket företag ska mockupen handla om?</Typography>
        {loading ? (
          <TextField
            fullWidth size="small"
            value={draft.context.customer_name}
            onChange={e => set('customer_name', e.target.value)}
            placeholder="T.ex. Jernhusen, Vasakronan..."
            sx={inputSx}
          />
        ) : (
          <Autocomplete
            freeSolo
            options={companies}
            getOptionLabel={o => typeof o === 'string' ? o : o.name}
            value={companies.find(c => c.id === draft.context.customer_id) || draft.context.customer_name}
            onInputChange={(_, val) => set('customer_name', val)}
            onChange={(_, val) => {
              if (val && typeof val === 'object') {
                set('customer_name', val.name);
                set('customer_id', val.id);
              } else {
                set('customer_name', val || '');
                set('customer_id', null);
              }
            }}
            renderInput={params => (
              <TextField {...params} size="small" placeholder="Sök eller skriv kundnamn..." sx={inputSx} />
            )}
          />
        )}
      </Box>

      <Box>
        <Typography fontWeight={700} sx={{ color: 'white', mb: 0.5, fontSize: '0.9rem' }}>Situation *</Typography>
        <Typography sx={{ color: '#666', fontSize: '0.8rem', mb: 1.5 }}>Var befinner ni er i processen?</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {SITUATIONS.map(s => (
            <Box key={s.id} onClick={() => set('situation', s.id)} sx={{
              flex: '1 1 180px', p: 2, borderRadius: 2, cursor: 'pointer',
              border: `1px solid ${draft.context.situation === s.id ? APPLAB_ACCENT : 'rgba(255,255,255,0.1)'}`,
              bgcolor: draft.context.situation === s.id ? APPLAB_ACCENT + '11' : 'rgba(255,255,255,0.02)',
              transition: 'all 0.12s',
              '&:hover': { borderColor: APPLAB_ACCENT + '66' },
            }}>
              <Typography fontSize="1.4rem" mb={0.5}>{s.icon}</Typography>
              <Typography fontWeight={700} sx={{ color: 'white', fontSize: '0.88rem', mb: 0.3 }}>{s.label}</Typography>
              <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>{s.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ── Brief Assist panel ────────────────────────────────────────────────────────
function BriefAssistPanel({ draft, onApply, currentUser }) {
  const [mode, setMode]           = useState(null); // 'improve' | 'suggest' | null
  const [loading, setLoading]     = useState(false);
  const [improveResult, setImp]   = useState(null);
  const [suggestResult, setSug]   = useState(null);
  const [error, setError]         = useState(null);

  async function call(type) {
    setMode(type);
    setLoading(true);
    setError(null);
    setImp(null);
    setSug(null);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/applab/brief-assist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type,
          context: { customer_name: draft.context.customer_name, situation: draft.context.situation },
          current_brief: draft.brief.description || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Något gick fel');
      if (type === 'improve') setImp(data);
      else setSug(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function close() { setMode(null); setImp(null); setSug(null); setError(null); }

  const qualityColor = { good: '#4CAF50', decent: '#FF9800', weak: '#f44336' };
  const qualityLabel = { good: 'Bra brief', decent: 'Kan förbättras', weak: 'Behöver mer info' };

  return (
    <Box>
      {/* Buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          startIcon={loading && mode === 'improve' ? <CircularProgress size={12} sx={{ color: APPLAB_ACCENT }} /> : <AutoFixHighIcon sx={{ fontSize: 14 }} />}
          onClick={() => call('improve')}
          disabled={loading || !draft.brief.description?.trim()}
          sx={{
            color: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '11',
            border: `1px solid ${APPLAB_ACCENT}33`, textTransform: 'none',
            fontSize: '0.78rem', fontWeight: 600, px: 1.5,
            '&:hover': { bgcolor: APPLAB_ACCENT + '22' },
            '&.Mui-disabled': { color: '#444', bgcolor: 'transparent', borderColor: 'transparent' },
          }}
        >
          Förbättra briefen
        </Button>
        <Button
          size="small"
          startIcon={loading && mode === 'suggest' ? <CircularProgress size={12} sx={{ color: '#888' }} /> : <ListAltIcon sx={{ fontSize: 14 }} />}
          onClick={() => call('suggest')}
          disabled={loading}
          sx={{
            color: '#888', bgcolor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)', textTransform: 'none',
            fontSize: '0.78rem', px: 1.5,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: 'white' },
          }}
        >
          Vad brukar ingå?
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(244,67,54,0.08)', borderRadius: 1.5, border: '1px solid rgba(244,67,54,0.2)' }}>
          <Typography fontSize="0.75rem" sx={{ color: '#f44336' }}>{error}</Typography>
        </Box>
      )}

      {/* Improve result */}
      <Collapse in={!!improveResult}>
        {improveResult && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={qualityLabel[improveResult.overall_quality] || improveResult.overall_quality}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.68rem', fontWeight: 700,
                    bgcolor: (qualityColor[improveResult.overall_quality] || '#888') + '22',
                    color: qualityColor[improveResult.overall_quality] || '#888',
                  }}
                />
                {improveResult.quality_feedback && (
                  <Typography fontSize="0.75rem" sx={{ color: '#888' }}>{improveResult.quality_feedback}</Typography>
                )}
              </Box>
              <CloseIcon sx={{ fontSize: 16, color: '#555', cursor: 'pointer', '&:hover': { color: 'white' } }} onClick={close} />
            </Box>

            {(improveResult.suggestions || []).map((s, i) => (
              <Box key={i} sx={{ mb: 1.25, p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.06)' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: s.example_addition ? 0.75 : 0 }}>
                  <Chip
                    label={{ missing_info: 'Info', missing_section: 'Sektion', style_hint: 'Stil' }[s.type] || s.type}
                    size="small"
                    sx={{ height: 18, fontSize: '0.62rem', flexShrink: 0, mt: 0.1,
                      bgcolor: 'rgba(255,255,255,0.06)', color: '#888' }}
                  />
                  <Typography fontSize="0.78rem" sx={{ color: '#ccc', lineHeight: 1.5 }}>{s.message}</Typography>
                </Box>
                {s.example_addition && (
                  <Box
                    onClick={() => onApply(s.example_addition)}
                    sx={{
                      mt: 0.5, ml: 4, p: 1, borderRadius: 1,
                      bgcolor: APPLAB_ACCENT + '08', border: `1px solid ${APPLAB_ACCENT}22`,
                      cursor: 'pointer', '&:hover': { bgcolor: APPLAB_ACCENT + '16' },
                    }}
                  >
                    <Typography fontSize="0.73rem" sx={{ color: '#888', fontStyle: 'italic', lineHeight: 1.5 }}>
                      "{s.example_addition}"
                    </Typography>
                    <Typography fontSize="0.68rem" sx={{ color: APPLAB_ACCENT, mt: 0.25 }}>Lägg till i brief →</Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Collapse>

      {/* Suggest result */}
      <Collapse in={!!suggestResult}>
        {suggestResult && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography fontSize="0.8rem" fontWeight={700} sx={{ color: 'white' }}>Vad brukar ingå?</Typography>
              <CloseIcon sx={{ fontSize: 16, color: '#555', cursor: 'pointer', '&:hover': { color: 'white' } }} onClick={close} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {(suggestResult.suggestions || []).map((s, i) => (
                <Box
                  key={i}
                  onClick={() => onApply(s.title + ': ' + s.description)}
                  title={`${s.description} (${s.typical_for})`}
                  sx={{
                    px: 1.25, py: 0.6, borderRadius: 1.5, cursor: 'pointer',
                    bgcolor: s.relevance === 'high' ? APPLAB_ACCENT + '15' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${s.relevance === 'high' ? APPLAB_ACCENT + '44' : 'rgba(255,255,255,0.08)'}`,
                    '&:hover': { bgcolor: APPLAB_ACCENT + '25', borderColor: APPLAB_ACCENT + '66' },
                    transition: 'all 0.12s',
                  }}
                >
                  <Typography fontSize="0.75rem" sx={{ color: s.relevance === 'high' ? APPLAB_ACCENT : '#aaa', fontWeight: s.relevance === 'high' ? 600 : 400 }}>
                    {s.title}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography fontSize="0.68rem" sx={{ color: '#444', mt: 1.25 }}>Klicka på ett förslag för att lägga till i briefen.</Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

// ── Steg 2: Brief ─────────────────────────────────────────────────────────────
function Step2({ draft, setDraft, currentUser }) {
  const [link, setLink] = useState('');
  const situation = draft.context.situation;
  const example   = BRIEF_EXAMPLES[situation] || '';

  function setB(key, val) { setDraft(d => ({ ...d, brief: { ...d.brief, [key]: val } })); }

  function addLink() {
    const l = link.trim();
    if (!l) return;
    setB('reference_links', [...(draft.brief.reference_links || []), l]);
    setLink('');
  }

  function applyAssistText(text) {
    setDraft(d => ({
      ...d,
      brief: {
        ...d.brief,
        description: d.brief.description
          ? d.brief.description.trimEnd() + '\n\n' + text
          : text,
      },
    }));
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography fontWeight={700} sx={{ color: 'white', mb: 0.5, fontSize: '0.9rem' }}>Beskriv vad mockupen ska visa *</Typography>
        <Typography sx={{ color: '#666', fontSize: '0.8rem', mb: 1.5 }}>Vad ska finnas med? Vilken känsla vill du ge?</Typography>
        <TextField
          fullWidth multiline rows={5}
          value={draft.brief.description}
          onChange={e => setB('description', e.target.value)}
          placeholder={`Exempel: "${example}"`}
          sx={inputSx}
          inputProps={{ maxLength: 2000 }}
        />
        <Typography sx={{ color: '#444', fontSize: '0.72rem', textAlign: 'right', mt: 0.5 }}>
          {(draft.brief.description || '').length} / 2000
        </Typography>
        <Box
          onClick={() => setB('description', example)}
          sx={{ mt: 0.5, color: APPLAB_ACCENT, fontSize: '0.75rem', cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
        >
          Fyll i exempel →
        </Box>
      </Box>

      {/* Brief Assist */}
      <BriefAssistPanel draft={draft} onApply={applyAssistText} currentUser={currentUser} />

      <Box>
        <Typography fontWeight={700} sx={{ color: 'white', mb: 0.5, fontSize: '0.9rem' }}>Referenslänkar (valfritt)</Typography>
        <Typography sx={{ color: '#666', fontSize: '0.8rem', mb: 1.5 }}>
          Lägg till webbplatser eller dokument-URL:er för inspiration eller kontext.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            fullWidth size="small"
            value={link}
            onChange={e => setLink(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
            placeholder="https://..."
            sx={inputSx}
          />
          <IconButton onClick={addLink} sx={{ color: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '22', borderRadius: 1.5, px: 1.5 }}>
            <LinkIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {(draft.brief.reference_links || []).map((url, i) => (
            <Chip
              key={i} label={url} size="small"
              onDelete={() => setB('reference_links', draft.brief.reference_links.filter((_, j) => j !== i))}
              sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#ccc', fontSize: '0.72rem', maxWidth: 320 }}
            />
          ))}
        </Box>
      </Box>

      <BrandingSection draft={draft} setDraft={setDraft} />
    </Box>
  );
}

// ── Steg 3: Stil ──────────────────────────────────────────────────────────────
function Step3({ draft, setDraft }) {
  const [customSys, setCustomSys] = useState('');
  const { pool, addToPool } = useSoftwareTags();

  function setS(key, val) { setDraft(d => ({ ...d, style: { ...d.style, [key]: val } })); }
  function setSys(key, val) { setDraft(d => ({ ...d, systems: { ...d.systems, [key]: val } })); }

  function toggleSystem(name) {
    const cur = draft.systems.registered || [];
    setSys('registered', cur.includes(name) ? cur.filter(x => x !== name) : [...cur, name]);
  }

  function addCustom() {
    const s = customSys.trim();
    if (!s) return;
    // Add to global softwareTags pool if not already there
    addToPool(s);
    setSys('custom', [...(draft.systems.custom || []), s]);
    setCustomSys('');
  }

  const toneLabels = ['Mycket formell', 'Formell', 'Neutral', 'Informell', 'Vardaglig'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography fontWeight={700} sx={{ color: 'white', mb: 0.5, fontSize: '0.9rem' }}>Stilpreset</Typography>
        <Typography sx={{ color: '#666', fontSize: '0.8rem', mb: 1.5 }}>Välj den känsla som passar bäst.</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {PRESETS.map(p => (
            <Box key={p.id} onClick={() => setS('preset', p.id)} sx={{
              p: 2, borderRadius: 2, cursor: 'pointer',
              border: `1px solid ${draft.style.preset === p.id ? APPLAB_ACCENT : 'rgba(255,255,255,0.1)'}`,
              bgcolor: draft.style.preset === p.id ? APPLAB_ACCENT + '11' : 'rgba(255,255,255,0.02)',
              transition: 'all 0.12s', '&:hover': { borderColor: APPLAB_ACCENT + '66' },
            }}>
              <Typography fontSize="1.2rem" mb={0.5}>{p.icon}</Typography>
              <Typography fontWeight={700} sx={{ color: 'white', fontSize: '0.85rem', mb: 0.3 }}>{p.label}</Typography>
              <Typography sx={{ color: '#666', fontSize: '0.73rem' }}>{p.desc}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box>
        <Typography fontWeight={700} sx={{ color: 'white', mb: 0.5, fontSize: '0.9rem' }}>Tonalitet</Typography>
        <Typography sx={{ color: '#666', fontSize: '0.8rem', mb: 2 }}>
          Hur ska språket och utformningen kännas?
        </Typography>
        <Box sx={{ px: 1 }}>
          <Slider
            value={draft.style.tone}
            onChange={(_, v) => setS('tone', v)}
            min={-2} max={2} step={1}
            marks={toneLabels.map((l, i) => ({ value: i - 2, label: '' }))}
            sx={{
              color: APPLAB_ACCENT,
              '& .MuiSlider-thumb': { bgcolor: APPLAB_ACCENT },
              '& .MuiSlider-track': { bgcolor: APPLAB_ACCENT },
              '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography sx={{ color: '#555', fontSize: '0.72rem' }}>Formell</Typography>
            <Typography sx={{ color: APPLAB_ACCENT, fontSize: '0.78rem', fontWeight: 600 }}>
              {toneLabels[draft.style.tone + 2]}
            </Typography>
            <Typography sx={{ color: '#555', fontSize: '0.72rem' }}>Informell</Typography>
          </Box>
        </Box>
      </Box>

      <Box>
        <Typography fontWeight={700} sx={{ color: 'white', mb: 0.5, fontSize: '0.9rem' }}>System att visa (valfritt)</Typography>
        <Typography sx={{ color: '#666', fontSize: '0.8rem', mb: 1.5 }}>
          Vilka system används av kunden? AI:n inkluderar dem i gränssnittet.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {pool.length === 0 && (
            <Typography sx={{ color: '#444', fontSize: '0.75rem' }}>Laddar system från konsultprofiler…</Typography>
          )}
          {pool.map(name => {
            const active = (draft.systems.registered || []).includes(name);
            return (
              <Chip
                key={name} label={name} size="small"
                onClick={() => toggleSystem(name)}
                sx={{
                  bgcolor: active ? APPLAB_ACCENT + '22' : 'rgba(255,255,255,0.06)',
                  color: active ? APPLAB_ACCENT : '#888',
                  border: `1px solid ${active ? APPLAB_ACCENT + '55' : 'transparent'}`,
                  cursor: 'pointer', fontWeight: active ? 700 : 400,
                  '&:hover': { bgcolor: active ? APPLAB_ACCENT + '33' : 'rgba(255,255,255,0.1)' },
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small" fullWidth
            value={customSys}
            onChange={e => setCustomSys(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
            placeholder="Annat system (tryck Enter)..."
            sx={inputSx}
          />
        </Box>
        {(draft.systems.custom || []).length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
            {draft.systems.custom.map((s, i) => (
              <Chip
                key={i} label={s} size="small"
                onDelete={() => setSys('custom', draft.systems.custom.filter((_, j) => j !== i))}
                sx={{ bgcolor: APPLAB_ACCENT + '22', color: APPLAB_ACCENT, fontSize: '0.72rem' }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Review-steg ───────────────────────────────────────────────────────────────
function ReviewStep({ draft, generating }) {
  const sit = SITUATIONS.find(s => s.id === draft.context.situation);
  const pre = PRESETS.find(p => p.id === draft.style.preset);
  const sysList = [
    ...(draft.systems.registered || []),
    ...(draft.systems.custom || []),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ color: '#888', fontSize: '0.82rem' }}>Allt stämmer? Granska innan AI:n genererar.</Typography>

      {[
        { label: 'Kund', value: draft.context.customer_name || '—' },
        { label: 'Situation', value: sit ? `${sit.icon} ${sit.label}` : '—' },
        { label: 'Brief', value: draft.brief.description ? draft.brief.description.slice(0, 120) + (draft.brief.description.length > 120 ? '…' : '') : '—' },
        { label: 'Stil', value: pre ? `${pre.icon} ${pre.label}` : '—' },
        { label: 'System', value: sysList.length ? sysList.join(', ') : 'Inga valda' },
      ].map(({ label, value }) => (
        <Box key={label} sx={{ display: 'flex', gap: 2 }}>
          <Typography sx={{ color: '#555', fontSize: '0.8rem', width: 80, flexShrink: 0 }}>{label}</Typography>
          <Typography sx={{ color: '#ccc', fontSize: '0.82rem', flex: 1 }}>{value}</Typography>
        </Box>
      ))}

      <Box sx={{ mt: 1, p: 2, bgcolor: 'rgba(0,137,123,0.08)', borderRadius: 2, border: `1px solid ${APPLAB_ACCENT}22` }}>
        <Typography sx={{ color: '#666', fontSize: '0.78rem', lineHeight: 1.6 }}>
          AI:n skapar ett komplett HTML-dokument med fejkad men realistisk data. Det tar vanligtvis 15–40 sekunder.
          Mockupen sparas automatiskt och kan delas via PitchN.
        </Typography>
      </Box>
    </Box>
  );
}

// ── Input-stil ────────────────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    bgcolor: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT },
  },
  '& input::placeholder, & textarea::placeholder': { color: '#555', opacity: 1 },
};

// ── Huvud-wizard ──────────────────────────────────────────────────────────────
const STEPS = [
  { title: 'För vem?',         sub: 'Kund och situation' },
  { title: 'Vad ska den visa?', sub: 'Brief och kontext' },
  { title: 'Hur ska den kännas?', sub: 'Stil och system' },
  { title: 'Granska',          sub: 'Och generera' },
];

export default function MockupWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mappnCaseId = searchParams.get('caseId');
  const { currentUser } = useAuth();
  const [step, setStep]         = useState(0);
  const [draft, setDraft]       = useState(DEFAULT_DRAFT);
  const [generating, setGen]    = useState(false);
  const [error, setError]       = useState(null);
  const saveTimer = useRef(null);

  // Load initial draft — from MappN case if ?caseId is present, else from saved draft
  useEffect(() => {
    if (!currentUser) return;

    if (mappnCaseId) {
      async function loadFromCase() {
        const [cSnap, compSnap] = await Promise.all([
          getDoc(doc(db, 'mappnCases', mappnCaseId)),
          getDocs(query(collection(db, 'mappnCases', mappnCaseId, 'components'), orderBy('createdAt', 'desc'))),
        ]);
        if (!cSnap.exists()) return;
        const caseData = cSnap.data();
        const comps = compSnap.docs.map(d => d.data());

        const compLines = comps
          .filter(c => c.title)
          .slice(0, 12)
          .map(c => `- ${c.title}${c.description ? ': ' + c.description : ''}`)
          .join('\n');
        const description = `System: ${caseData.name || ''}\n\nKomponenter:\n${compLines}`.trim();

        const techSet = new Set();
        for (const t of (caseData.techStack || [])) { if (t.name) techSet.add(t.name); }
        for (const comp of comps) {
          for (const t of (comp.techStack || [])) { if (t.name) techSet.add(t.name); }
        }

        setDraft(d => ({
          ...d,
          context: {
            ...d.context,
            customer_name: d.context.customer_name || caseData.customerName || '',
          },
          brief: { ...d.brief, description },
          systems: { ...d.systems, custom: [...techSet] },
        }));
      }
      loadFromCase().catch(() => {});
    } else {
      loadDraft(currentUser.uid).then(d => { if (d) setDraft(d); }).catch(() => {});
    }
  }, [currentUser, mappnCaseId]);

  // Auto-save draft on changes (debounced)
  const autosave = useCallback((d) => {
    if (!currentUser) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveDraft(currentUser.uid, d).catch(() => {}), 1200);
  }, [currentUser]);

  useEffect(() => { autosave(draft); }, [draft, autosave]);

  function canProceed() {
    if (step === 0) return draft.context.customer_name.trim().length > 0 && draft.context.situation;
    if (step === 1) return draft.brief.description.trim().length > 10;
    return true;
  }

  async function generate() {
    if (!currentUser || generating) return;
    setGen(true);
    setError(null);

    try {
      // Create the app document first (status: 'generating')
      const mockup_data = {
        context:  draft.context,
        brief:    draft.brief,
        style:    draft.style,
        branding: draft.branding,
        systems:  draft.systems,
        current_version: null,
        versions_count: 0,
      };
      const appId = await createMockupApp({
        uid:         currentUser.uid,
        displayName: currentUser.displayName || currentUser.email || '',
        name:        `MockupN – ${draft.context.customer_name}`,
        mockup_data,
      });

      // Navigate immediately to generating page; it will trigger the actual generation
      navigate(`/applab/mockup/${appId}/generating`, {
        state: { draft, mockup_data },
      });
    } catch (e) {
      setError(e.message);
      setGen(false);
    }
  }

  const stepContent = [
    <Step1 key={0} draft={draft} setDraft={setDraft} />,
    <Step2 key={1} draft={draft} setDraft={setDraft} currentUser={currentUser} />,
    <Step3 key={2} draft={draft} setDraft={setDraft} />,
    <ReviewStep key={3} draft={draft} generating={generating} />,
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#111', color: 'white', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', borderBottom: '1px solid rgba(255,255,255,0.06)', px: 3, py: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box onClick={() => navigate('/applab/new')} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', color: '#888', '&:hover': { color: 'white' }, fontSize: '0.85rem' }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} /> Tillbaka
          </Box>
          <Typography sx={{ color: '#444' }}>›</Typography>
          <Typography fontWeight={700} fontSize="0.9rem">
            App<span style={{ color: APPLAB_ORANGE }}>LAB</span>
            <span style={{ color: '#555', fontWeight: 400 }}> · MockupN</span>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', py: 5, px: 2 }}>
        <Box sx={{ width: '100%', maxWidth: 620 }}>
          {/* Step indicator */}
          <Box sx={{ mb: 4 }}>
            <StepIndicator current={step} total={STEPS.length} />
          </Box>

          {/* Step header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" fontWeight={900} sx={{ mb: 0.5 }}>{STEPS[step].title}</Typography>
            <Typography sx={{ color: '#666', fontSize: '0.88rem' }}>{STEPS[step].sub}</Typography>
          </Box>

          {/* Step content */}
          {stepContent[step]}

          {/* Error */}
          {error && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(239,83,80,0.1)', borderRadius: 1.5, border: '1px solid rgba(239,83,80,0.3)' }}>
              <Typography sx={{ color: '#ef5350', fontSize: '0.82rem' }}>{error}</Typography>
            </Box>
          )}

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
            {step > 0 ? (
              <Button
                onClick={() => setStep(s => s - 1)}
                startIcon={<ArrowBackIcon />}
                sx={{ color: '#888', textTransform: 'none', fontWeight: 600 }}
              >
                Tillbaka
              </Button>
            ) : <Box />}

            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed()}
                endIcon={<ArrowForwardIcon />}
                variant="contained"
                sx={{ bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' }, textTransform: 'none', fontWeight: 700, px: 3 }}
              >
                Fortsätt
              </Button>
            ) : (
              <Button
                onClick={generate}
                disabled={generating || !canProceed()}
                variant="contained"
                startIcon={generating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <span>✨</span>}
                sx={{ bgcolor: APPLAB_ACCENT, '&:hover': { bgcolor: '#00796B' }, textTransform: 'none', fontWeight: 700, px: 4 }}
              >
                {generating ? 'Förbereder…' : 'Generera mockup'}
              </Button>
            )}
          </Box>

          <Typography sx={{ color: '#333', fontSize: '0.72rem', textAlign: 'center', mt: 2 }}>
            Utkast sparas automatiskt
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
